import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(220, 38, 38, 0.8), rgba(185, 28, 28, 0.8)), url("/images/srv_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;
    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: white;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;

  .service-card {
    background: white;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-10px);
    }

    .service-image {
      height: 250px;
      background-size: cover;
      background-position: center;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(45deg, rgba(220, 38, 38, 0.8), rgba(185, 28, 28, 0.6));
      }
    }

    .service-content {
      padding: 2rem;
      position: relative;

      h3 {
        color: #dc2626;
        margin-bottom: 1rem;
        font-size: 1.3rem;
      }

      p {
        color: #666;
        line-height: 1.6;
        margin-bottom: 1.5rem;
      }

      .service-features {
        list-style: none;
        padding: 0;
        margin-bottom: 1.5rem;

        li {
          padding: 0.5rem 0;
          color: #666;
          position: relative;
          padding-left: 1.5rem;

          &:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #dc2626;
            font-weight: bold;
          }
        }
      }

      .service-link {
        display: inline-block;
        background: #dc2626;
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        text-decoration: none;
        font-weight: 500;
        transition: background 0.3s ease;

        &:hover {
          background: #b91c1c;
        }
      }
    }
  }
`;

const CTASection = styled.section`
  padding: 80px 0;
  background: #f8f9fa;
  text-align: center;

  h2 {
    color: #dc2626;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    .btn {
      padding: 15px 30px;
      font-size: 1.1rem;
    }
  }
`;

const Services = () => {
  const services = [
    {
      id: "mobile-fitting",
      title: "Mobile Tyre Fitting",
      description: "Our expert technicians come to your depot or roadside to fit truck and trailer tyres when it suits your schedule.",
      image: "/images/ser_mtf.webp",
      features: [
        "On-site tyre fitting service",
        "Professional mobile technicians",
        "Quality tyres from trusted brands",
        "Convenient scheduling",
        "Competitive pricing",
      ],
    },
    // Breakdown cover removed per request
    {
      id: "new-tyres",
      title: "Reliable New Tyres",
      description: "High-quality tyres from trusted brands to ensure your safety and performance on the road.",
      image: "/images/ser_rnt.webp",
      features: ["Premium tyre brands", "Competitive pricing", "Professional installation", "Quality guarantee", "Expert recommendations"],
    },
    {
      id: "puncture-repair",
      title: "Tyre Puncture Repair",
      description: "Professional puncture repair services to extend the life of your tyres and save you money.",
      image: "/images/ser_tpr.webp",
      features: ["Quick puncture repairs", "Mobile repair service", "Cost-effective solutions", "Professional assessment", "Safety-first approach"],
    },
    {
      id: "tyre-rotation",
      title: "Tyre Rotation",
      description: "Regular tyre rotation services to ensure even wear and maximize tyre lifespan.",
      image: "/images/ser_tirot.webp",
      features: ["Professional tyre rotation", "Extended tyre life", "Improved performance", "Cost savings", "Regular maintenance schedules"],
    },
  ];

  return (
    <>
      <PageHeader>
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            Our Services
          </motion.h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <div className="section-title">
            <h2>Comprehensive truck tyre solutions</h2>
            <p>
              In today's fast-paced world, not only is tyre technology rapidly evolving, but there is an ever-increasing number of high-quality
              alternatives to traditional brands available.
            </p>
          </div>

          <ServicesGrid>
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className="service-card"
                id={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}>
                <div
                  className="service-image"
                  style={{ backgroundImage: `url(${service.image})` }}
                />
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>{feature}</li>
                    ))}
                  </ul>
                  <Link
                    to="/get-quote"
                    className="service-link">
                    Get Quote
                  </Link>
                </div>
              </motion.div>
            ))}
          </ServicesGrid>
        </div>
      </ContentSection>

      <CTASection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <h2>Need Professional Tyre Services?</h2>
            <p>
              Contact us today for expert advice, competitive quotes, and reliable service. Our team is ready to help you with all your tyre and truck
              solution needs.
            </p>
            <div className="cta-buttons">
              <Link
                to="/get-quote"
                className="btn">
                Get a Quote
              </Link>
              <Link
                to="/contact"
                className="btn btn-outline">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </CTASection>
    </>
  );
};

export default Services;
